import React, { useState, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import { Calculator, Users, Clock, TrendingUp, Plus, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";

const LICENSE_PRICE = 6805;

const formatCurrency = (val) =>
  val.toLocaleString("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

function useAnimatedNumber(value, duration = 600) {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const start = display;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (value - start) * eased);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => {
      if (ref.current) cancelAnimationFrame(ref.current);
    };
  }, [value, duration]);
  return display;
}

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0, 0, 0.2, 1] } },
};

const RecruitmentTab = ({ onResult }) => {
  const intl = useIntl();
  const [position, setPosition] = useState("");
  const [feePercent, setFeePercent] = useState(25);
  const [salaireBrut, setSalaireBrut] = useState(0);
  const [treiziemeEnabled, setTreiziemeEnabled] = useState(false);
  const multiplier = treiziemeEnabled ? 13.92 : 12.92;
  const [voiture, setVoiture] = useState(0);
  const [mobilityBudget, setMobilityBudget] = useState(0);
  const [chequeRepas, setChequeRepas] = useState(0);
  const [fraisRepresentation, setFraisRepresentation] = useState(0);
  const [healthAssurance, setHealthAssurance] = useState(0);
  const [groupAssurancePct, setGroupAssurancePct] = useState(0);
  const [bonusAnnuel, setBonusAnnuel] = useState(0);
  const [autresAvantages, setAutresAvantages] = useState(0);

  const groupAssuranceAnnuel = salaireBrut * (groupAssurancePct / 100) * 12;

  const salairesAnnuels =
    salaireBrut * multiplier +
    voiture +
    mobilityBudget * 12 +
    chequeRepas * 220 +
    fraisRepresentation * 12 +
    healthAssurance +
    groupAssuranceAnnuel +
    bonusAnnuel +
    autresAvantages;

  const totalFees = salairesAnnuels * ((Number(feePercent) || 0) / 100);
  const licensesEquivalent = totalFees / LICENSE_PRICE;

  useEffect(() => {
    onResult({
      salary: salairesAnnuels,
      fees: totalFees,
      licenses: licensesEquivalent,
      feePercent,
      mode: "recruitment",
      savings: 0,
    });
  }, [salairesAnnuels, totalFees, licensesEquivalent, feePercent]);

  const fields = [
    { labelId: "cost_calculator.recruitment.car_annual", value: voiture, setter: setVoiture },
    { labelId: "cost_calculator.recruitment.mobility_budget_monthly", value: mobilityBudget, setter: setMobilityBudget },
    { labelId: "cost_calculator.recruitment.meal_voucher_daily", value: chequeRepas, setter: setChequeRepas },
    { labelId: "cost_calculator.recruitment.representation_fees_monthly", value: fraisRepresentation, setter: setFraisRepresentation },
    { labelId: "cost_calculator.recruitment.health_insurance_annual", value: healthAssurance, setter: setHealthAssurance },
    { labelId: "cost_calculator.recruitment.group_insurance_pct", value: groupAssurancePct, setter: setGroupAssurancePct },
    { labelId: "cost_calculator.recruitment.annual_bonus", value: bonusAnnuel, setter: setBonusAnnuel },
    { labelId: "cost_calculator.recruitment.other_benefits_annual", value: autresAvantages, setter: setAutresAvantages },
  ];

  return (
    <Box
      component={motion.div}
      variants={stagger}
      initial="hidden"
      animate="show"
      sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
    >
      {/* Position recherchée */}
      <Box component={motion.div} variants={fadeUp}>
        <Box
          component="label"
          sx={{
            fontSize: "0.75rem",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            color: "hsl(220, 10%, 46%)",
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage id="cost_calculator.recruitment.position_label" />
        </Box>
        <Box
          component="input"
          placeholder={intl.formatMessage({ id: "cost_calculator.recruitment.position_placeholder" })}
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          sx={{
            display: "flex",
            height: "36px",
            width: "100%",
            borderRadius: "6px",
            border: "1px solid hsla(220, 13%, 91%, 0.6)",
            bgcolor: "hsl(0, 0%, 100%)",
            px: "12px",
            py: "4px",
            fontSize: { xs: "1rem", md: "0.875rem" },
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            color: "hsl(220, 25%, 12%)",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            transition: "border-color 0.15s ease",
            outline: "none",
            mt: "6px",
            "&::placeholder": { color: "hsl(220, 10%, 46%)" },
            "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
            MozAppearance: "textfield",
          }}
        />
      </Box>

      {/* Frais d'agence */}
      <Box component={motion.div} variants={fadeUp}>
        <Box
          component="label"
          sx={{
            fontSize: "0.75rem",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            color: "hsl(220, 10%, 46%)",
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage id="cost_calculator.recruitment.agency_fee_label" />
        </Box>
        <Box
          component="input"
          type="number"
          min={0}
          max={100}
          value={feePercent}
          onChange={(e) => setFeePercent(e.target.value === "" ? "" : Number(e.target.value))}
          sx={{
            display: "flex",
            height: "36px",
            width: "100%",
            borderRadius: "6px",
            border: "1px solid hsla(220, 13%, 91%, 0.6)",
            bgcolor: "hsl(0, 0%, 100%)",
            px: "12px",
            py: "4px",
            fontSize: { xs: "1rem", md: "0.875rem" },
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            color: "hsl(220, 25%, 12%)",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            transition: "border-color 0.15s ease",
            outline: "none",
            mt: "6px",
            "&::placeholder": { color: "hsl(220, 10%, 46%)" },
            "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
            MozAppearance: "textfield",
          }}
        />
      </Box>

      {/* Grid of salary fields */}
      <Box
        component={motion.div}
        variants={fadeUp}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: "12px",
        }}
      >
        {/* Salaire brut mensuel */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "18px" }}>
            <Box
              component="label"
              sx={{
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(220, 10%, 46%)",
                lineHeight: 1.5,
              }}
            >
              <FormattedMessage id="cost_calculator.recruitment.gross_salary_label" values={{ multiplier: treiziemeEnabled ? "13.92" : "12.92" }} />
            </Box>
          </Box>
          <Box
            component="input"
            type="number"
            value={salaireBrut || ""}
            onChange={(e) => setSalaireBrut(Number(e.target.value) || 0)}
            placeholder="0"
            sx={{
              display: "flex",
              height: "36px",
              width: "100%",
              borderRadius: "6px",
              border: "1px solid hsla(220, 13%, 91%, 0.6)",
              bgcolor: "hsl(0, 0%, 100%)",
              px: "12px",
              py: "4px",
              fontSize: { xs: "1rem", md: "0.875rem" },
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              transition: "border-color 0.15s ease",
              outline: "none",
              mt: "4px",
              "&::placeholder": { color: "hsl(220, 10%, 46%)" },
              "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
              MozAppearance: "textfield",
            }}
          />
        </Box>

        {/* 13ème mois toggle */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03, duration: 0.25 }}
          sx={{ display: "flex", alignItems: "center", pt: "22px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              component="button"
              type="button"
              onClick={() => setTreiziemeEnabled(!treiziemeEnabled)}
              sx={{
                position: "relative",
                width: "32px",
                height: "18px",
                borderRadius: "9999px",
                transition: "background-color 0.15s ease",
                flexShrink: 0,
                bgcolor: treiziemeEnabled ? "hsl(145, 63%, 42%)" : "hsl(220, 13%, 91%)",
                border: "none",
                cursor: "pointer",
                p: 0,
              }}
            >
              <Box
                component={motion.div}
                animate={{ x: treiziemeEnabled ? 14 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                sx={{
                  position: "absolute",
                  top: "2px",
                  left: "2px",
                  width: "14px",
                  height: "14px",
                  borderRadius: "9999px",
                  bgcolor: "#fff",
                  boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                }}
              />
            </Box>
            <Box
              component="label"
              sx={{
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: treiziemeEnabled ? "hsl(220, 10%, 46%)" : "hsla(220, 10%, 46%, 0.5)",
                lineHeight: 1.5,
              }}
            >
              <FormattedMessage id="cost_calculator.recruitment.thirteenth_month" />{treiziemeEnabled ? " (×13.92)" : ""}
            </Box>
          </Box>
        </Box>

        {/* Dynamic benefit fields */}
        {fields.map((f, i) => (
          <Box
            key={f.label}
            component={motion.div}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03, duration: 0.25 }}
          >
            <Box
              component="label"
              sx={{
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(220, 10%, 46%)",
                lineHeight: 1.5,
              }}
            >
              {intl.formatMessage({ id: f.labelId })}
            </Box>
            <Box
              component="input"
              type="number"
              min={0}
              value={f.value || ""}
              onChange={(e) => f.setter(Number(e.target.value) || 0)}
              placeholder="0"
              sx={{
                display: "flex",
                height: "36px",
                width: "100%",
                borderRadius: "6px",
                border: "1px solid hsla(220, 13%, 91%, 0.6)",
                bgcolor: "hsl(0, 0%, 100%)",
                px: "12px",
                py: "4px",
                fontSize: { xs: "1rem", md: "0.875rem" },
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(220, 25%, 12%)",
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                transition: "border-color 0.15s ease",
                outline: "none",
                mt: "4px",
                "&::placeholder": { color: "hsl(220, 10%, 46%)" },
                "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                MozAppearance: "textfield",
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const createWorker = () => ({
  id: Math.random().toString(36).slice(2, 9),
  salaireSemaine: 500,
  nbSemaines: 3,
  coefficient: 2.2,
});

const InterimTab = ({ onResult }) => {
  const intl = useIntl();
  const [workers, setWorkers] = useState(() => [createWorker(), createWorker(), createWorker()]);

  const updateWorker = (id, patch) => {
    setWorkers((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  };
  const removeWorker = (id) => {
    setWorkers((prev) => (prev.length > 1 ? prev.filter((w) => w.id !== id) : prev));
  };
  const addWorker = () => setWorkers((prev) => [...prev, createWorker()]);

  const directCost = workers.reduce((sum, w) => sum + w.salaireSemaine * w.nbSemaines, 0);
  const totalInterimCost = workers.reduce(
    (sum, w) => sum + w.salaireSemaine * (Number(w.coefficient) || 1) * w.nbSemaines,
    0,
  );
  const savings = totalInterimCost - directCost;
  const licensesEquivalent = totalInterimCost / LICENSE_PRICE;
  const nbInterimaires = workers.length;

  useEffect(() => {
    onResult({
      salary: directCost,
      fees: totalInterimCost,
      licenses: licensesEquivalent,
      mode: "interim",
      savings,
      nbInterimaires,
      totalInterimCost,
    });
  }, [directCost, totalInterimCost, licensesEquivalent, savings, nbInterimaires]);

  return (
    <Box
      component={motion.div}
      variants={stagger}
      initial="hidden"
      animate="show"
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      <AnimatePresence initial={false}>
        {workers.map((w, idx) => (
          <Box
            key={w.id}
            component={motion.div}
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.25 }}
            sx={{
              borderRadius: "12px",
              border: "1px solid hsla(220, 13%, 91%, 0.6)",
              bgcolor: "hsla(0, 0%, 100%, 0.5)",
              p: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box
                component="label"
                sx={{
                  fontSize: "0.75rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(145, 63%, 42%)",
                  lineHeight: 1.5,
                }}
              >
                <FormattedMessage id="cost_calculator.interim.worker_label" values={{ number: idx + 1 }} />
              </Box>
              {workers.length > 1 && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => removeWorker(w.id)}
                  aria-label={intl.formatMessage({ id: "cost_calculator.interim.delete_worker" })}
                  sx={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "hsl(220, 10%, 46%)",
                    display: "flex",
                    p: 0,
                    transition: "color 0.15s ease",
                    "&:hover": { color: "hsl(0, 84%, 60%)" },
                  }}
                >
                  <Trash2 size={14} />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: "12px",
              }}
            >
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 10%, 46%)",
                    lineHeight: 1.5,
                  }}
                >
                  <FormattedMessage id="cost_calculator.interim.weekly_salary" />
                </Box>
                <Box
                  component="input"
                  type="number"
                  min={0}
                  value={w.salaireSemaine || ""}
                  onChange={(e) => updateWorker(w.id, { salaireSemaine: Number(e.target.value) || 0 })}
                  placeholder="0"
                  sx={{
                    display: "flex",
                    height: "36px",
                    width: "100%",
                    borderRadius: "6px",
                    border: "1px solid hsla(220, 13%, 91%, 0.6)",
                    bgcolor: "hsl(0, 0%, 100%)",
                    px: "12px",
                    py: "4px",
                    fontSize: { xs: "1rem", md: "0.875rem" },
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 25%, 12%)",
                    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                    transition: "border-color 0.15s ease",
                    outline: "none",
                    mt: "4px",
                    "&::placeholder": { color: "hsl(220, 10%, 46%)" },
                    "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                    MozAppearance: "textfield",
                  }}
                />
              </Box>
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 10%, 46%)",
                    lineHeight: 1.5,
                  }}
                >
                  <FormattedMessage id="cost_calculator.interim.number_of_weeks" />
                </Box>
                <Box
                  component="input"
                  type="number"
                  value={w.nbSemaines || ""}
                  onChange={(e) => updateWorker(w.id, { nbSemaines: Number(e.target.value) || 0 })}
                  placeholder="1"
                  sx={{
                    display: "flex",
                    height: "36px",
                    width: "100%",
                    borderRadius: "6px",
                    border: "1px solid hsla(220, 13%, 91%, 0.6)",
                    bgcolor: "hsl(0, 0%, 100%)",
                    px: "12px",
                    py: "4px",
                    fontSize: { xs: "1rem", md: "0.875rem" },
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 25%, 12%)",
                    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                    transition: "border-color 0.15s ease",
                    outline: "none",
                    mt: "4px",
                    "&::placeholder": { color: "hsl(220, 10%, 46%)" },
                    "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                    MozAppearance: "textfield",
                  }}
                />
              </Box>
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 10%, 46%)",
                    lineHeight: 1.5,
                  }}
                >
                  <FormattedMessage id="cost_calculator.interim.coefficient" />
                </Box>
                <Box
                  component="input"
                  type="number"
                  step={0.1}
                  value={w.coefficient === "" ? "" : w.coefficient}
                  onChange={(e) =>
                    updateWorker(w.id, { coefficient: e.target.value === "" ? "" : Number(e.target.value) })
                  }
                  sx={{
                    display: "flex",
                    height: "36px",
                    width: "100%",
                    borderRadius: "6px",
                    border: "1px solid hsla(220, 13%, 91%, 0.6)",
                    bgcolor: "hsl(0, 0%, 100%)",
                    px: "12px",
                    py: "4px",
                    fontSize: { xs: "1rem", md: "0.875rem" },
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 25%, 12%)",
                    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                    transition: "border-color 0.15s ease",
                    outline: "none",
                    mt: "4px",
                    "&::placeholder": { color: "hsl(220, 10%, 46%)" },
                    "&:focus": { borderColor: "hsl(145, 63%, 42%)", boxShadow: "none" },
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                    MozAppearance: "textfield",
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </AnimatePresence>

      <Button
        type="button"
        variant="outlined"
        onClick={addWorker}
        disableRipple
        sx={{
          width: "100%",
          borderStyle: "dashed",
          borderColor: "hsla(220, 13%, 91%, 0.6)",
          color: "hsl(220, 25%, 12%)",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          fontSize: "0.875rem",
          textTransform: "none",
          borderRadius: "6px",
          py: "8px",
          transition: "border-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            borderColor: "hsl(145, 63%, 42%)",
            color: "hsl(145, 63%, 42%)",
            bgcolor: "transparent",
          },
        }}
      >
        <Plus size={16} style={{ marginRight: "8px" }} /> <FormattedMessage id="cost_calculator.interim.add_worker" />
      </Button>
    </Box>
  );
};

const ResultPanel = ({
  salary,
  fees,
  licenses,
  feePercent,
  mode,
  nbInterimaires,
}) => {
  const animatedLicenses = useAnimatedNumber(licenses);
  const animatedFees = useAnimatedNumber(fees);
  const animatedSalary = useAnimatedNumber(salary);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Summary card */}
      <Box
        sx={{
          borderRadius: "12px",
          bgcolor: "hsla(220, 14%, 96%, 0.4)",
          border: "1px solid hsla(220, 13%, 91%, 0.6)",
          p: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              color: "hsl(220, 10%, 46%)",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id={mode === "interim" ? "cost_calculator.result.direct_salary_total" : "cost_calculator.result.salary_package"} />
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: "0.875rem",
              color: "hsl(220, 25%, 12%)",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            {formatCurrency(Math.round(animatedSalary))}
          </Box>
        </Box>

        <Box sx={{ height: "1px", bgcolor: "hsla(220, 13%, 91%, 0.6)" }} />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              color: "hsl(220, 10%, 46%)",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            {mode === "interim" && nbInterimaires
              ? <FormattedMessage id="cost_calculator.result.total_interim_cost" values={{ count: nbInterimaires }} />
              : <FormattedMessage id="cost_calculator.result.agency_fee" values={{ percent: feePercent }} />}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: "0.875rem",
              color: "hsl(220, 25%, 12%)",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            {formatCurrency(Math.round(animatedFees))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          borderRadius: "16px",
          border: "2px solid hsla(145, 63%, 42%, 0.3)",
          background: "linear-gradient(to bottom right, hsla(145, 63%, 42%, 0.05), hsla(145, 63%, 42%, 0.15))",
          p: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <TrendingUp style={{ color: "hsl(145, 63%, 42%)", marginBottom: "8px" }} size={20} />
        <Box
          component="p"
          sx={{
            fontSize: "0.75rem",
            color: "hsl(220, 10%, 46%)",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            mb: "12px",
            mt: 0,
          }}
        >
          <FormattedMessage id="cost_calculator.result.equivalent_licenses" />
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: { xs: "3rem", md: "3.75rem" },
            color: "hsl(145, 63%, 42%)",
            fontVariantNumeric: "tabular-nums",
            fontFamily: "Segoe UI Black, system-ui, sans-serif",
            lineHeight: 1,
          }}
        >
          {animatedLicenses > 0 ? animatedLicenses.toFixed(1) : "0"}
        </Box>
        <Box
          component="p"
          sx={{
            fontSize: "0.875rem",
            color: "hsl(220, 10%, 46%)",
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            mt: "8px",
            mb: 0,
          }}
        >
          {animatedLicenses > 1 ? <FormattedMessage id="cost_calculator.result.license_plural" /> : <FormattedMessage id="cost_calculator.result.license_singular" />}{" "}<FormattedMessage id="cost_calculator.result.at_price" values={{ price: formatCurrency(LICENSE_PRICE) }} />
        </Box>
        {licenses >= 1 && (
          <Box
            component="p"
            sx={{
              fontSize: "0.75rem",
              color: "hsl(145, 63%, 42%)",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              mt: "12px",
              mb: 0,
            }}
          >
            {mode === "recruitment"
              ? <FormattedMessage id="cost_calculator.result.months_access" values={{ months: (licenses * 12).toFixed(0) }} />
              : <FormattedMessage id="cost_calculator.result.save_by_recruiting" />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CostCalculator = () => {
  const [activeTab, setActiveTab] = useState("recruitment");
  const [result, setResult] = useState({
    salary: 0,
    fees: 0,
    licenses: 0,
    feePercent: 0,
    mode: "recruitment",
    savings: 0,
  });

  const tabs = [
    { id: "recruitment", labelId: "cost_calculator.tab_recruitment", icon: Users },
    { id: "interim", labelId: "cost_calculator.tab_interim", icon: Clock },
  ];

  return (
    <Box
      component="section"
      id="calculator"
      sx={{
        py: { xs: "80px", md: "112px" },
        bgcolor: "hsla(220, 14%, 96%, 0.2)",
      }}
    >
      <Box sx={{ mx: "auto", px: "16px", maxWidth: "1152px" }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: "48px" }}>
          <Box
            component={motion.span}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              px: "16px",
              py: "6px",
              borderRadius: "9999px",
              bgcolor: "hsla(145, 63%, 42%, 0.1)",
              color: "hsl(145, 63%, 42%)",
              fontSize: "0.75rem",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              mb: "16px",
            }}
          >
            <Calculator size={14} /> <FormattedMessage id="cost_calculator.badge" />
          </Box>

          <Box
            component={motion.h2}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              color: "hsl(220, 25%, 12%)",
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id="cost_calculator.title" />
          </Box>

          <Box
            component={motion.p}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            sx={{
              color: "hsl(220, 10%, 46%)",
              maxWidth: "672px",
              mx: "auto",
              fontSize: "1.125rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              mt: 0,
              mb: 0,
            }}
          >
            <FormattedMessage id="cost_calculator.description" />
          </Box>
        </Box>

        {/* Tab switcher */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: "40px" }}>
          <Box
            sx={{
              display: "inline-flex",
              borderRadius: "12px",
              bgcolor: "hsl(0, 0%, 100%)",
              p: "6px",
              gap: "4px",
            }}
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Box
                  key={tab.id}
                  component="button"
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    px: "24px",
                    py: "12px",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    transition: "color 0.15s ease",
                    color: isActive ? "hsl(0, 0%, 100%)" : "hsl(220, 10%, 46%)",
                    bgcolor: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {isActive && (
                    <Box
                      component={motion.div}
                      layoutId="tab-indicator"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "8px",
                        bgcolor: "hsl(145, 63%, 42%)",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
                        zIndex: -1,
                      }}
                    />
                  )}
                  <Icon size={16} />
                  <FormattedMessage id={tab.labelId} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Split layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
            gap: "32px",
            alignItems: "start",
          }}
        >
          {/* Left: Inputs */}
          <Box
            component={motion.div}
            transition={{ duration: 0.3 }}
            sx={{
              borderRadius: "16px",
              bgcolor: "hsl(0, 0%, 100%)",
              border: "1px solid hsla(220, 13%, 91%, 0.6)",
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              p: { xs: "24px", md: "32px" },
            }}
          >
            <AnimatePresence exitBeforeEnter>
              {activeTab === "recruitment" ? (
                <Box
                  key="recruitment"
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.25 }}
                >
                  <RecruitmentTab onResult={setResult} />
                </Box>
              ) : (
                <Box
                  key="interim"
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.25 }}
                >
                  <InterimTab onResult={setResult} />
                </Box>
              )}
            </AnimatePresence>
          </Box>

          {/* Right: Result panel (sticky) */}
          <Box
            sx={{
              position: { lg: "sticky" },
              top: { lg: "30%" },
              alignSelf: { lg: "start" },
            }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.4 }}
              sx={{
                borderRadius: "16px",
                bgcolor: "hsl(0, 0%, 100%)",
                border: "1px solid hsla(220, 13%, 91%, 0.6)",
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                p: "24px",
                width: "100%",
              }}
            >
              <ResultPanel {...result} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CostCalculator;
